/**
 * Game Pass visibility in PAM is enabled when either platform-config flag is on:
 * - `additional_features.game_pass` (consumer)
 * - `additional_features.game_pass_pam` (PAM admin)
 *
 * Both are read from `/admin-panel/platform/v1/config`.
 */
export function isGamePassPamEnabled(featuresStatusConfig) {
  return featuresStatusConfig?.additional_features?.game_pass_pam === true;
}

export function isGamePassFeatureEnabled(featuresStatusConfig) {
  if (!featuresStatusConfig) {
    return false;
  }

  const { additional_features: additionalFeatures } = featuresStatusConfig;

  return (
    additionalFeatures?.game_pass === true ||
    additionalFeatures?.game_pass_pam === true
  );
}

/**
 * PAM Game Pass admin surfaces unlock for any admin holding a Game Pass
 * permission. PAM-team whitelist membership is no longer required — it only
 * powers the additive `game_pass_pam` bypass for GA Seasons below, which lets
 * whitelisted members in before the granular permission ships on their role.
 */
export function canAccessGamePassAdmin({
  featuresStatusConfig,
  adminPermissions,
  isPamTeamWhitelisted,
}) {
  const gamePassPermissions = [
    "game_pass.configuration.seasons",
    "game_pass.configuration.daily",
    "game_pass.configuration.monthly",
    "game_pass.configuration.style",
    "game_pass.reporting",
  ];

  const hasGamePassPermission =
    Array.isArray(adminPermissions) &&
    gamePassPermissions.some((permission) =>
      adminPermissions.includes(permission),
    );

  if (hasGamePassPermission) {
    return true;
  }

  return canBypassGamePassSeasonsViaPam({
    featuresStatusConfig,
    isPamTeamWhitelisted,
  });
}

export const GAME_PASS_SEASONS_PERMISSION = "game_pass.configuration.seasons";

/**
 * PAM-team bypass for GA Seasons when `game_pass_pam` is on. Does not grant
 * access to other Game Pass permissions (daily, monthly, style, reporting).
 */
export function canBypassGamePassSeasonsViaPam({
  featuresStatusConfig,
  isPamTeamWhitelisted,
}) {
  return (
    isPamTeamWhitelisted && isGamePassPamEnabled(featuresStatusConfig)
  );
}

/**
 * When `game_pass_pam` is on, whitelisted PAM team members can reach GA
 * Seasons without the granular permission having shipped on their role yet.
 */
export function canAccessGamePassSeasons({
  featuresStatusConfig,
  adminPermissions,
  isPamTeamWhitelisted,
}) {
  if (
    canBypassGamePassSeasonsViaPam({
      featuresStatusConfig,
      isPamTeamWhitelisted,
    })
  ) {
    return true;
  }

  return (
    Array.isArray(adminPermissions) &&
    adminPermissions.includes(GAME_PASS_SEASONS_PERMISSION)
  );
}

/**
 * Sidebar option ids that `game_pass_pam` unlocks for whitelisted PAM team
 * members before granular permissions ship on their role.
 */
export const GAME_PASS_PAM_UNLOCKED_OPTION_IDS = new Set([
  "game-pass-configuration-seasons",
]);
